import { format, isSameDay } from 'date-fns';
import { BankTransactionListItem } from 'types/bank-transfers.ts';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';

/** Left-to-right order of the `Group by:` control. */
export const BANK_TRANSFER_GROUP_BY_OPTIONS = ['date', 'uploadedAt', 'none'] as const;

export type BankTransferGroupBy = (typeof BANK_TRANSFER_GROUP_BY_OPTIONS)[number];

/**
 * The payment date, not the upload instant. A statement is read against the day the money moved; when
 * it was imported says something about the importer, not about the transfer.
 */
export const DEFAULT_BANK_TRANSFER_GROUP_BY: BankTransferGroupBy = 'date';

/** The list's default order, and its fallback within every group. */
const DEFAULT_SORT = 'entryBookingDate,desc';

/**
 * Last-resort ordering. The controller's `id ASC` default applies only to a request that carries no
 * sort at all, and this list always carries one — without an explicit tie-breaker two rows equal on
 * every key above it can swap places between page requests, so the scroller fetches one twice and
 * never sees the other.
 */
const ID_TIE_BREAKER = 'id,asc';

/**
 * The server sort each grouping forces.
 *
 * Rows have to arrive grouped: the list pages 100 at a time, so a group whose rows are scattered
 * through the result set would open a fresh ribbon on every page. Both groupings are keyed by their
 * own sort field, so neither needs a second key to keep a day whole.
 */
const GROUP_SORT: Record<Exclude<BankTransferGroupBy, 'none'>, string> = {
    date: DEFAULT_SORT,
    uploadedAt: 'createdAt,desc',
};

function sortField(sort: string): string {
    return sort.split(',')[0] ?? '';
}

/**
 * The `sort` params one fetch sends: the group's sort first, then the user's column sort, then the
 * date and the id.
 *
 * The group leads so its rows cannot straddle a page boundary, which leaves the column sort ordering
 * rows WITHIN each group — except when the user sorted by the very field the grouping is keyed on,
 * where their direction replaces it outright.
 */
export function composeBankTransferSort(groupBy: BankTransferGroupBy, columnSort: string): string[] {
    const sorts: string[] = [];
    if (groupBy !== 'none') {
        const groupSort = GROUP_SORT[groupBy];
        sorts.push(sortField(groupSort) === sortField(columnSort) ? columnSort : groupSort);
    }
    if (columnSort && !sorts.includes(columnSort)) {
        sorts.push(columnSort);
    }
    if (!sorts.some((sort) => sortField(sort) === sortField(DEFAULT_SORT))) {
        sorts.push(DEFAULT_SORT);
    }
    return [...sorts, ID_TIE_BREAKER];
}

/**
 * What identifies a row's group. Both groupings are a date; the ungrouped list puts every row in `''`.
 *
 * `createdAt` is an instant in the database and in the filter that ranges over it, but the list
 * payload narrows it to a `LocalDate` before serialising, so it arrives as `yyyy-MM-dd` and keys an
 * upload DAY directly — there is no time part here to trim off.
 */
export function bankTransferGroupKeyOf(groupBy: BankTransferGroupBy): (transfer: BankTransactionListItem) => string {
    switch (groupBy) {
        case 'date':
            return (transfer) => transfer.entryBookingDate ?? '';
        case 'uploadedAt':
            return (transfer) => transfer.createdAt ?? '';
        // Flat list: every row lands in the one group, whose ribbon is not rendered.
        case 'none':
        default:
            return () => '';
    }
}

export interface GroupedTransfers {
    transfers: BankTransactionListItem[];
    /**
     * `entryAmount` summed per currency over the rows PASSED IN — nothing is converted, and nothing
     * asks the server. The list pages, so a long group's figure grows as the reader scrolls; the
     * filtered set's real per-currency totals are the ones the totals row above the table shows.
     */
    currencyTotals: Map<string, number>;
}

/**
 * Groups transfers in the order they arrive, summing each group's amounts per currency.
 *
 * Group ORDER follows the row order — first appearance of each key — but membership is by key, so a
 * key that reappears after another rejoins its first group rather than opening a second. What that
 * relies on is the caller sorting so a key's rows are contiguous, since the ribbon is drawn above a
 * group's FIRST row; {@link composeBankTransferSort} is what guarantees it.
 */
export function groupBankTransfers(
    transfers: BankTransactionListItem[],
    keyOf: (transfer: BankTransactionListItem) => string
): Map<string, GroupedTransfers> {
    const grouped = new Map<string, GroupedTransfers>();
    transfers.forEach((transfer) => {
        const key = keyOf(transfer);
        let group = grouped.get(key);
        if (!group) {
            group = { transfers: [], currencyTotals: new Map() };
            grouped.set(key, group);
        }
        group.transfers.push(transfer);
        const currency = transfer.entryAmountCurrencyTypeCode;
        if (currency) {
            // The amount is serialised as a string, so it is coerced rather than added directly.
            const amount = Number(transfer.entryAmount) || 0;
            group.currencyTotals.set(currency, (group.currencyTotals.get(currency) ?? 0) + amount);
        }
    });
    return grouped;
}

/** A group's heading: today by name, any other day by weekday, both followed by the date. */
export function formatGroupDate(date: string): string {
    const dateObj = dateStringToDateObject(date);
    return isSameDay(dateObj, new Date())
        ? `${t('time.today')}, ${format(dateObj, t('timecards.list.group-date-format-without-day'))}`
        : format(dateObj, t('timecards.list.group-date-format'));
}
